import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  createInvitation,
  getInvitationTemplates,
  getUserInvitation,
  updateInvitation,
  uploadInvitationCover,
} from "../../utils/invitationsApi";
import { useAuth } from "../../contexts/AuthContext";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { getInvitationQuotes } from "../../utils/quotesApi";
import {
  FONT_SETTING_KEYS,
  buildGoogleFontsUrl,
  // ensureValidFontVariant,
  // getAvailableFontStyles,
  // getAvailableFontWeights,
  // sanitizeFontSettings,
  isGoogleFontFamily,
} from "./invitationFonts";
import InvitationAgendaEditor from "./InvitationAgendaEditor";
import { cyrillicToLatinSlug } from "../../utils/cyrillicToLatin";
import { PRODUCT_IDS } from "../../utils/entitlements";

const DEFAULT_PRIMARY = "#b89b5e";
const DEFAULT_SECONDARY = "#2d2a26";
const FALLBACK_TITLE = "Вашата покана";
const API_BASE = process.env.REACT_APP_API_BASE;
const BOOLEAN_FIELDS = ["is_public", "agenda_shown"];
const MAX_COVER_FILE_SIZE = 10 * 1024 * 1024;
const AGENDA_TIME_REGEX = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

// const FONT_SETTING_LABELS = {
//   title: "Наслов",
//   subtitle: "Поднаслов",
//   body: "Текст",
// };
const GOOGLE_FONT_LINK_ID = "invitation-google-fonts";

const parseTemplateConfig = (template) => {
  if (!template) return {};
  const raw = template.json_structure;
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw;
};

const derivePaletteFromConfig = (config) => {
  const palette = config?.palette || {};
  return {
    primary: palette.primary || palette.accent || DEFAULT_SECONDARY,
    secondary: palette.accent || palette.secondary || DEFAULT_PRIMARY,
  };
};

const getPaletteFromTemplate = (template) =>
  derivePaletteFromConfig(parseTemplateConfig(template));

const normalizeFontOption = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    return { family: value };
  }
  if (typeof value === "object") {
    const { family, weight, style } = value;
    return {
      ...(family ? { family } : {}),
      ...(weight ? { weight } : {}),
      ...(style ? { style } : {}),
    };
  }
  return null;
};

// const fontDescriptorsEqual = (a = {}, b = {}) =>
//   JSON.stringify(a || {}) === JSON.stringify(b || {});

// const describeFontOption = (option) => {
//   if (!option || typeof option !== "object") return "Default";
//   const parts = [];
//   if (option.family) {
//     parts.push(option.family);
//   }
//   const detail = [option.weight, option.style].filter(Boolean).join(", ");
//   if (detail) {
//     parts.push(`(${detail})`);
//   }
//   return parts.join(" ") || "Custom";
// };

// const COLOR_PICKER_SX = {
//   "& input[type='color']": {
//     padding: 0,
//     width: "100%",
//     height: "56px",
//     minHeight: "56px",
//     border: "none",
//     cursor: "pointer",
//     backgroundColor: "transparent",
//   },
//   "& .MuiInputBase-root": {
//     padding: "4px 12px",
//   },
// };

const buildDefaultTitle = (brideName, groomName, options = {}) => {
  const fallback =
    Object.prototype.hasOwnProperty.call(options, "fallback") && options.fallback !== undefined
      ? options.fallback
      : FALLBACK_TITLE;
  const bride = brideName?.trim();
  const groom = groomName?.trim();
  if (bride && groom) {
    return `${bride} & ${groom}`;
  }
  if (bride) {
    return bride;
  }
  if (groom) {
    return groom;
  }
  return fallback;
};

const InvitationBuilder = () => {
  const navigate = useNavigate();
  const { invitationId } = useParams();
  const isEdit = Boolean(invitationId);
  const [searchParams] = useSearchParams();
  const { wedding, hasProduct, entitlementsLoading, paymentsEnforced } = useAuth();

  const [templates, setTemplates] = useState([]);
  const [existingInvitation, setExistingInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdInvitation, setCreatedInvitation] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [form, setForm] = useState({
    template_id: "",
    title: "",
    venue_name: "",
    message: "",
    is_public: true,
    font_settings: {},
    primary_color: "",
    secondary_color: "",
    agenda_shown: false,
    agenda_items: [],
  });
  const [profileTitle, setProfileTitle] = useState("");
  const [fetchedWeddingDate, setFetchedWeddingDate] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [applyingQuoteId, setApplyingQuoteId] = useState("");
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState("");
  const [coverUploadError, setCoverUploadError] = useState("");
  const coverInputRef = useRef(null);
  const formRef = useRef(null);
  const [isCoverPreviewDialogOpen, setCoverPreviewDialogOpen] = useState(false);

  const defaultTitle = useMemo(() => {
    return buildDefaultTitle(wedding?.bride_name, wedding?.groom_name, {
      fallback: "",
    });
  }, [wedding]);
  const computedDefaultTitle = useMemo(() => {
    if (defaultTitle) {
      return defaultTitle;
    }
    if (profileTitle) {
      return profileTitle;
    }
    return FALLBACK_TITLE;
  }, [defaultTitle, profileTitle]);

  useEffect(() => {
    if (isEdit || profileTitle || defaultTitle) {
      return;
    }

    let active = true;
    const loadProfileNames = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/users/{user_id}`, {
          method: "GET",
        });
        if (!response || !response.ok) {
          return;
        }
        const data = await response.json();
        if (!active || !data) {
          return;
        }
        const combined = buildDefaultTitle(data.bride_name, data.groom_name, {
          fallback: "",
        });
        if (combined) {
          setProfileTitle(combined);
        }
      } catch (err) {
        console.warn("Unable to fetch user names for invitation title default", err);
      }
    };

    loadProfileNames();
    return () => {
      active = false;
    };
  }, [isEdit, profileTitle, defaultTitle]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const templateData = await getInvitationTemplates();
        setTemplates(templateData);
        try {
          const quoteOptions = await getInvitationQuotes();
          const normalizedQuotes = (Array.isArray(quoteOptions) ? quoteOptions : [])
            .filter((quote) => quote && (quote.text || quote.message))
            .map((quote, index) => {
              const text = quote.text || quote.message || "";
              return {
                key: quote.key || quote.id || String(index),
                text,
              };
            });
          setQuotes(normalizedQuotes);
        } catch (quoteError) {
          console.warn("Unable to fetch invitation quotes", quoteError);
        }

        const resolveTemplate = (id) =>
          templateData.find((tpl) => tpl.id === id) || templateData[0] || null;

        if (isEdit && invitationId) {
          const invitation = await getUserInvitation(invitationId);
          setExistingInvitation(invitation);
          const templateForPalette = resolveTemplate(invitation.template_id);
          const paletteDefaults = getPaletteFromTemplate(templateForPalette);
          setForm({
            template_id:
              invitation.template_id ||
              templateData[0]?.id ||
              searchParams.get("templateId") ||
              "",
            title: invitation.title || "",
            venue_name: invitation.venue_name || "",
            message: invitation.message || "",
            is_public:
              invitation.is_public === undefined ? true : invitation.is_public,
            font_settings: invitation.font_settings || {},
            primary_color:
              invitation.theme_colors?.primary || paletteDefaults.primary,
            secondary_color:
              invitation.theme_colors?.secondary || paletteDefaults.secondary,
            agenda_shown:
              invitation.agenda_shown === undefined
                ? false
                : Boolean(invitation.agenda_shown),
            agenda_items: invitation.agenda_items || [],
          });
        } else {
          const queryTemplateId = searchParams.get("templateId");
          const initialTemplate =
            templateData.find((tpl) => tpl.id === queryTemplateId)?.id ||
            templateData[0]?.id ||
            "";
          const templateForPalette = resolveTemplate(initialTemplate);
          const paletteDefaults = getPaletteFromTemplate(templateForPalette);
          setForm((prev) => ({
            ...prev,
            template_id: initialTemplate,
            font_settings: {},
            primary_color: paletteDefaults.primary,
            secondary_color: paletteDefaults.secondary,
          }));
        }
      } catch (err) {
        console.error(err);
        setError(
          err.message || "Настана грешка при вчитување на податоците."
        );
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isEdit, invitationId, searchParams]);

  useEffect(() => {
    if (coverFile) {
      return;
    }
    const nextPreview = existingInvitation?.cover_image_url || "";
    setCoverPreview(nextPreview);
  }, [existingInvitation, coverFile]);

  useEffect(() => {
    if (!coverFile) {
      return undefined;
    }
    const objectUrl = URL.createObjectURL(coverFile);
    setCoverPreview(objectUrl);
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [coverFile]);

  useEffect(() => {
    if (!coverPreview) {
      setCoverPreviewDialogOpen(false);
    }
  }, [coverPreview]);

  useEffect(() => {
    if (wedding?.date) {
      setFetchedWeddingDate(wedding.date);
      return;
    }

    let cancelled = false;
    const fetchWeddingDate = async () => {
      try {
        const response = await fetchWithAuth(`${API_BASE}/weddings/my`, {
          method: "GET",
        });
        if (!response || !response.ok) {
          return;
        }
        const data = await response.json();
        if (cancelled) {
          return;
        }
        if (data?.date) {
          setFetchedWeddingDate(data.date);
        }
      } catch (error) {
        console.warn("Unable to fetch wedding date for invitation", error);
      }
    };

    fetchWeddingDate();
    return () => {
      cancelled = true;
    };
  }, [wedding]);

  useEffect(() => {
    if (isEdit || !computedDefaultTitle) {
      return;
    }
    if (form.title === computedDefaultTitle) {
      return;
    }
    const shouldOverwrite =
      !form.title || form.title === FALLBACK_TITLE;
    if (!shouldOverwrite) {
      return;
    }
    setForm((prev) => ({
      ...prev,
      title: computedDefaultTitle,
    }));
  }, [isEdit, computedDefaultTitle, form.title]);

  const selectedTemplate = useMemo(
    () => templates.find((tpl) => tpl.id === form.template_id),
    [templates, form.template_id]
  );

  const selectedTemplateConfig = useMemo(
    () => parseTemplateConfig(selectedTemplate),
    [selectedTemplate],
  );

  const selectedPaletteDefaults = useMemo(
    () => derivePaletteFromConfig(selectedTemplateConfig),
    [selectedTemplateConfig],
  );

  const fontOptions = useMemo(() => {
    const fontSettings = selectedTemplateConfig?.font_settings;
    if (!fontSettings || typeof fontSettings !== "object") {
      return {};
    }
    return Object.entries(fontSettings).reduce((acc, [key, value]) => {
      const asArray = Array.isArray(value) ? value : [value];
      const normalized = asArray
        .map((entry) => normalizeFontOption(entry))
        .filter(Boolean);
      if (normalized.length) {
        acc[key] = normalized;
      }
      return acc;
    }, {});
  }, [selectedTemplateConfig]);

  useEffect(() => {
    setForm((prev) => {
      const optionEntries = Object.entries(fontOptions);
      if (!optionEntries.length) {
        return prev;
      }

      const nextFonts = { ...(prev.font_settings || {}) };
      let changed = false;

      optionEntries.forEach(([key, options]) => {
        if (!options || !options.length) {
          return;
        }
        if (!nextFonts[key]) {
          nextFonts[key] = options[0];
          changed = true;
        }
      });

      if (!changed) {
        return prev;
      }

      return { ...prev, font_settings: nextFonts };
    });
  }, [fontOptions]);

  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }
    const selections = FONT_SETTING_KEYS.map(
      (key) => form.font_settings?.[key]
    ).filter(
      (descriptor) =>
        descriptor?.family && isGoogleFontFamily(descriptor.family)
    );
    const href = buildGoogleFontsUrl(selections);
    const existingLink = document.getElementById(GOOGLE_FONT_LINK_ID);
    if (!href) {
      if (existingLink) {
        existingLink.remove();
      }
      return undefined;
    }
    if (existingLink && existingLink.getAttribute("href") === href) {
      return undefined;
    }
    const link = document.createElement("link");
    link.id = GOOGLE_FONT_LINK_ID;
    link.rel = "stylesheet";
    link.href = href;
    if (existingLink) {
      existingLink.remove();
    }
    document.head.appendChild(link);
    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [form.font_settings]);

  const handleChange = (field) => (event) => {
    const value = BOOLEAN_FIELDS.includes(field)
      ? event.target.checked
      : event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleTemplateChange = (event) => {
    const templateId = event.target.value;
    const template = templates.find((tpl) => tpl.id === templateId) || null;
    const palette = getPaletteFromTemplate(template);
    setForm((prev) => ({
      ...prev,
      template_id: templateId,
      font_settings: {},
      primary_color: palette.primary,
      secondary_color: palette.secondary,
    }));
  };

  // const handleColorChange = (field) => (event) => {
  //   const value = event.target.value;
  //   setForm((prev) => ({
  //     ...prev,
  //     [field]: value,
  //   }));
  // };

  const handleAgendaItemsChange = (items) => {
    setForm((prev) => ({
      ...prev,
      agenda_items: items,
    }));
  };

  const scrollToTop = () => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleCoverFileChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (event.target) {
      event.target.value = "";
    }
    if (!file) {
      return;
    }
    if (file.size > MAX_COVER_FILE_SIZE) {
      setCoverUploadError("Максималната големина за датотеката е 10MB.");
      return;
    }
    setCoverUploadError("");
    setCoverFile(file);
  };

  const handleCoverRemove = () => {
    setCoverFile(null);
    setCoverUploadError("");
  };

  const handleCoverPreviewClick = () => {
    if (coverPreview) {
      setCoverPreviewDialogOpen(true);
    }
  };

  const handleCoverPreviewDialogClose = () => {
    setCoverPreviewDialogOpen(false);
  };

  const uploadCoverIfNeeded = async (targetInvitationId) => {
    if (!coverFile || !targetInvitationId) {
      return null;
    }
    const fileToUpload = coverFile;
    try {
      const response = await uploadInvitationCover(targetInvitationId, fileToUpload);
      setCoverUploadError("");
      if (response?.cover_image_url) {
        setCoverPreview(response.cover_image_url);
        if (isEdit) {
          setExistingInvitation((prev) =>
            prev ? { ...prev, cover_image_url: response.cover_image_url } : prev
          );
        } else {
          setCreatedInvitation((prev) =>
            prev ? { ...prev, cover_image_url: response.cover_image_url } : prev
          );
        }
      }
      return response;
    } catch (uploadError) {
      console.error("Unable to upload invitation cover", uploadError);
      setCoverUploadError(
        uploadError.message ||
          "Настана грешка при прикачување на насловната фотографија."
      );
      return null;
    } finally {
      setCoverFile(null);
    }
  };

  const handleAgendaShownChange = (shown) => {
    setForm((prev) => ({
      ...prev,
      agenda_shown: shown,
    }));
  };

  // const getFontSelectValue = (key) => {
  //   const options = fontOptions[key] || [];
  //   const current = form.font_settings?.[key];
  //   const matchIndex = options.findIndex((option) =>
  //     fontDescriptorsEqual(option, current)
  //   );
  //   return matchIndex >= 0 ? String(matchIndex) : "";
  // };

  // const getCustomFontFamilyValue = (key) => {
  //   const descriptor = form.font_settings?.[key];
  //   if (descriptor?.family && isGoogleFontFamily(descriptor.family)) {
  //     return descriptor.family;
  //   }
  //   return "";
  // };

  // const getCustomFontWeightValue = (key) => {
  //   const descriptor = form.font_settings?.[key];
  //   if (
  //     descriptor?.family &&
  //     isGoogleFontFamily(descriptor.family) &&
  //     descriptor.weight
  //   ) {
  //     return String(descriptor.weight);
  //   }
  //   return "";
  // };

  // const getCustomFontStyleValue = (key) => {
  //   const descriptor = form.font_settings?.[key];
  //   if (descriptor?.family && isGoogleFontFamily(descriptor.family)) {
  //     return descriptor.style === "italic" ? "italic" : "normal";
  //   }
  //   return "";
  // };

  // const getGoogleFontStylesForField = (key) => {
  //   const family = getCustomFontFamilyValue(key);
  //   if (!family) {
  //     return [];
  //   }
  //   return getAvailableFontStyles(family);
  // };

  // const getGoogleFontWeightsForField = (key) => {
  //   const family = getCustomFontFamilyValue(key);
  //   if (!family) {
  //     return [];
  //   }
  //   const style = getCustomFontStyleValue(key) || undefined;
  //   return getAvailableFontWeights(family, style);
  // };

  // const handleFontSettingSelect = (key) => (event) => {
  //   const idx = Number(event.target.value);
  //   const options = fontOptions[key] || [];
  //   if (Number.isNaN(idx) || !options[idx]) {
  //     return;
  //   }
  //   const selectedOption = options[idx];
  //   setForm((prev) => ({
  //     ...prev,
  //     font_settings: {
  //       ...(prev.font_settings || {}),
  //       [key]: selectedOption,
  //     },
  //   }));
  // };

  // const handleCustomFontFamilyChange = (key) => (event) => {
  //   const family = event.target.value;
  //   setForm((prev) => {
  //     const nextFonts = { ...(prev.font_settings || {}) };
  //     if (!family) {
  //       if (nextFonts[key]) {
  //         delete nextFonts[key];
  //         return {
  //           ...prev,
  //           font_settings: sanitizeFontSettings(nextFonts),
  //         };
  //       }
  //       return prev;
  //     }
  //     const normalized = ensureValidFontVariant(family);
  //     if (!normalized) {
  //       return prev;
  //     }
  //     const current = nextFonts[key];
  //     if (fontDescriptorsEqual(current, normalized)) {
  //       return prev;
  //     }
  //     nextFonts[key] = normalized;
  //     return {
  //       ...prev,
  //       font_settings: sanitizeFontSettings(nextFonts),
  //     };
  //   });
  // };

  // const handleCustomFontWeightChange = (key) => (event) => {
  //   const weight = Number(event.target.value);
  //   setForm((prev) => {
  //     const current = prev.font_settings?.[key];
  //     if (!current?.family || Number.isNaN(weight)) {
  //       return prev;
  //     }
  //     const normalized = ensureValidFontVariant(
  //       current.family,
  //       weight,
  //       current.style
  //     );
  //     if (!normalized || fontDescriptorsEqual(current, normalized)) {
  //       return prev;
  //     }
  //     const nextFonts = { ...(prev.font_settings || {}) };
  //     nextFonts[key] = normalized;
  //     return {
  //       ...prev,
  //       font_settings: sanitizeFontSettings(nextFonts),
  //     };
  //   });
  // };

  // const handleCustomFontStyleChange = (key) => (event) => {
  //   const style = event.target.value;
  //   setForm((prev) => {
  //     const current = prev.font_settings?.[key];
  //     if (!current?.family) {
  //       return prev;
  //     }
  //     const normalized = ensureValidFontVariant(
  //       current.family,
  //       current.weight,
  //       style
  //     );
  //     if (!normalized || fontDescriptorsEqual(current, normalized)) {
  //       return prev;
  //     }
  //     const nextFonts = { ...(prev.font_settings || {}) };
  //     nextFonts[key] = normalized;
  //     return {
  //       ...prev,
  //       font_settings: sanitizeFontSettings(nextFonts),
  //     };
  //   });
  // };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccessMessage(null);

    const hasInvitationsProduct = hasProduct(PRODUCT_IDS.INVITATIONS);
    if (!isEdit && !hasInvitationsProduct) {
      setError(
        "За креирање покана потребен е активен пакет. Отворете Ценовник и активирајте го пакетот за покани."
      );
      setSubmitting(false);
      return;
    }

    const fallbackPrimary = selectedPaletteDefaults.primary;
    const fallbackSecondary = selectedPaletteDefaults.secondary;
    const sanitizedAgendaItems = Array.isArray(form.agenda_items)
      ? form.agenda_items
          .map((item, index) => {
            const title = item?.title?.trim() || "";
            const description = item?.description?.trim() || "";
            const time = item?.time?.trim() || "";
            const address = item?.address?.trim() || "";
            const existingStep = item?.step?.trim();
            const fallbackSlug = cyrillicToLatinSlug(title);
            const step =
              existingStep ||
              fallbackSlug ||
              (title ? `step_${index + 1}` : "");
            return {
              step,
              title,
              description,
              time,
              address,
            };
          })
          .filter(
            (item) =>
              item.step ||
              item.title ||
              item.description ||
              item.time ||
              item.address
          )
      : [];
    const invalidAgendaItem = sanitizedAgendaItems.find(
      (item) => item.time && !AGENDA_TIME_REGEX.test(item.time)
    );
    if (invalidAgendaItem) {
      setError(
        "Проверете ја агендата: времето мора да биде во формат HH:MM (пример 12:30 или 19:00)."
      );
      setSubmitting(false);
      return;
    }
    const resolvedWeddingDate = wedding?.date || fetchedWeddingDate || null;
    const eventDateIso = resolvedWeddingDate
      ? new Date(resolvedWeddingDate).toISOString()
      : null;
    if (!eventDateIso) {
      setError(
        "За покана прво внесете датум на свадба во Профил."
      );
      setSubmitting(false);
      return;
    }

    const payload = {
      template_id: form.template_id || null,
      title: form.title.trim(),
      event_date: eventDateIso,
      venue_name: form.venue_name.trim() || null,
      message: form.message.trim() || null,
      is_public: form.is_public,
      font_settings:
        form.font_settings && Object.keys(form.font_settings).length
          ? form.font_settings
          : null,
      // Allow overriding theme colors via pickers
      theme_colors: {
        primary: form.primary_color || fallbackPrimary,
        secondary: form.secondary_color || fallbackSecondary,
      },
      agenda_shown: Boolean(form.agenda_shown),
      agenda_items: sanitizedAgendaItems,
    };

    try {
      if (isEdit && invitationId) {
        const result = await updateInvitation(invitationId, payload);
        setExistingInvitation(result);
        setSuccessMessage({
          text: "Поканата е ажурирана.",
          slug: result.slug,
        });
        await uploadCoverIfNeeded(invitationId);
        scrollToTop();
      } else {
        const result = await createInvitation(payload);
        setCreatedInvitation(result);
        const newInvitationId = result?.id || result?.invitation_id;
        await uploadCoverIfNeeded(newInvitationId);
        scrollToTop();
      }
    } catch (err) {
      console.error(err);
      const normalizedErrorMessage = String(err?.message || "").toLowerCase();
      if (
        paymentsEnforced &&
        !isEdit &&
        (normalizedErrorMessage.includes("403") ||
          normalizedErrorMessage.includes("forbidden"))
      ) {
        setError(
          "Немате активен пакет за покани. Посетете Ценовник за да активирате пристап."
        );
        return;
      }
      setError(
        err.message ||
          "Настана грешка при зачувување на поканата. Обидете се повторно."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box className="invitation-page-container">
        <CircularProgress />
        <Typography mt={2}>Вчитување...</Typography>
      </Box>
    );
  }

  const headline = isEdit ? "Уреди покана" : "Креирај покана";
  const subHeadline = isEdit
    ? "Ажурирајте ги деталите и споделете го вашиот линк повторно."
    : "Одберете шаблон и внесете ги деталите за вашата прослава.";
  const resolvedWeddingDate = wedding?.date || fetchedWeddingDate || null;
  const weddingDateLabel = resolvedWeddingDate
    ? new Date(resolvedWeddingDate).toLocaleString("mk-MK", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";
  const hasInvitationsProduct = hasProduct(PRODUCT_IDS.INVITATIONS);
  const canCreateInvitation = isEdit || hasInvitationsProduct;

  return (
    <>
      <Box className="invitation-page-container">
      <Stack spacing={2} alignItems="center" className="invitation-header">
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: "2.4rem", sm: "2.6rem", md: "2.8rem" } }}
        >
          {headline}
        </Typography>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          {subHeadline}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="text"
            onClick={() => navigate("/invitations")}
            sx={{
              color: "var(--primary-color)",
              "&:hover": {
                backgroundColor: "rgba(227, 95, 135, 0.08)",
              },
            }}
          >
            ← Назад кон покани
          </Button>
          <Button
            variant="contained"
            disabled={submitting || !canCreateInvitation}
            onClick={() => formRef.current?.requestSubmit()}
            sx={{
              backgroundColor: "var(--primary-color)",
              "&:hover": {
                backgroundColor: "var(--primary-color-dark)",
              },
            }}
          >
            {submitting
              ? "Се зачувува..."
              : isEdit
              ? "Зачувај измени"
              : "Креирај покана"}
          </Button>
        </Stack>
      </Stack>

      {!isEdit && !entitlementsLoading && !hasInvitationsProduct && (
        <Alert
          severity="info"
          sx={{ mb: 3, maxWidth: 720, mx: "auto", boxSizing: "border-box", width: "fit-content" }}
        >
          За креирање покана потребен е активен пакет. Отворете{" "}
          <Button
            component={RouterLink}
            to="/pricing"
            size="small"
            sx={{ p: 0, minWidth: "auto", verticalAlign: "baseline" }}
          >
            Ценовник
          </Button>
          .
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, maxWidth: 720, mx: "auto", boxSizing: "border-box", width: "fit-content" }}
        >
          {error}
        </Alert>
      )}
      <Alert
        severity={resolvedWeddingDate ? "info" : "warning"}
        sx={{ mb: 3, maxWidth: 720, mx: "auto", boxSizing: "border-box", width: "fit-content" }}
      >
        {resolvedWeddingDate ? (
          <>
            Датум за поканата: <strong>{weddingDateLabel}</strong>. Се менува само во{" "}
            <Button
              component={RouterLink}
              to="/profile"
              size="small"
              sx={{ p: 0, minWidth: "auto", verticalAlign: "baseline" }}
            >
              Профил
            </Button>
            .
          </>
        ) : (
          <>
            Немате внесен датум на свадба. Внесете го во{" "}
            <Button
              component={RouterLink}
              to="/profile"
              size="small"
              sx={{ p: 0, minWidth: "auto", verticalAlign: "baseline" }}
            >
              Профил
            </Button>{" "}
            за да зачувате покана.
          </>
        )}
      </Alert>

        {isEdit && successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 3, maxWidth: 720, mx: "auto", boxSizing: "border-box", width: "fit-content" }}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            <span>{successMessage.text}</span>
            {successMessage.slug && (
              <Button
                size="small"
                component="a"
                href={`/invite/${successMessage.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "var(--primary-color)",
                  "&:hover": {
                    backgroundColor: "rgba(227, 95, 135, 0.08)",
                  },
                }}
              >
                Преглед
              </Button>
            )}
          </Stack>
        </Alert>
      )}

        {!isEdit && createdInvitation ? (
        <Card className="invitation-card success-card">
          <CardContent>
            <Typography variant="h5" gutterBottom>
              Поканата е креирана!
            </Typography>
            <Typography variant="body1" gutterBottom>
              Споделете го следниот линк со гостите:
            </Typography>
            <Typography
              variant="body2"
              className="invitation-share-link"
              gutterBottom
            >
              {`${window.location.origin}/invite/${createdInvitation.slug}`}
            </Typography>
            <Stack direction="row" spacing={2} mt={2}>
              <Button
                variant="contained"
                component="a"
                href={`/invite/${createdInvitation.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: "var(--primary-color)",
                  "&:hover": { backgroundColor: "var(--primary-color-dark)" },
                }}
              >
                Отвори покана
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/invitations")}
                sx={{
                  borderColor: "var(--primary-color)",
                  color: "var(--primary-color)",
                  "&:hover": {
                    borderColor: "var(--primary-color-dark)",
                    backgroundColor: "rgba(227, 95, 135, 0.04)",
                  },
                }}
              >
                Готово
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <Card className="invitation-card form-card">
          <CardContent>
            <form ref={formRef} onSubmit={handleSubmit}>
              <Grid container spacing={3} justifyContent="center" direction="column" alignItems="stretch">
                {/* Title */}
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Наслов"
                    value={form.title}
                    onChange={handleChange("title")}
                    required
                    sx={{ maxWidth: { xs: 'none', md: 720 }, mx: { xs: 0, md: 'auto' } }}
                  />
                </Grid>

                {/* Location */}
                <Grid item xs={12} md={12}>
                  <TextField
                    fullWidth
                    label="Локација"
                    value={form.venue_name}
                    onChange={handleChange("venue_name")}
                    sx={{ maxWidth: { xs: 'none', md: 720 }, mx: { xs: 0, md: 'auto' } }}
                  />
                </Grid>

                {/* Template */}
                <Grid item xs={12} md={12}>
                  <TextField
                    select
                    fullWidth
                    label="Шаблон"
                    value={form.template_id}
                    onChange={handleTemplateChange}
                    SelectProps={{ native: true }}
                    required
                    sx={{ maxWidth: { xs: 'none', md: 720 }, mx: { xs: 0, md: 'auto' } }}
                  >
                    {templates.map((template) => (
                      <option value={template.id} key={template.id}>
                        {template.name}
                      </option>
                    ))}
                  </TextField>
                </Grid>

                {(selectedTemplate?.thumbnail_url ||
                  selectedTemplateConfig?.backgroundImage) && (
                  <Grid item xs={12}>
                    <div
                      className="invitation-template-preview"
                      style={{
                        backgroundImage: `url(${
                          selectedTemplateConfig?.backgroundImage ||
                          selectedTemplate.thumbnail_url
                        })`,
                        width: "80%",
                        maxWidth: 450,
                        minHeight: 600,
                        margin: "0 auto",
                      }}
                    >
                      <Typography variant="subtitle2" color="inherit">
                        Преглед на шаблон
                      </Typography>
                    </div>
                  </Grid>
                )}

                {/*
                  {Object.keys(fontOptions).length > 0 && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Фонтови
                      </Typography>
                      <Grid container spacing={2}>
                        {Object.entries(fontOptions).map(([key, options]) => (
                          <Grid item xs={12} md={4} key={key}>
                            <TextField
                              select
                              fullWidth
                              label={`Фонт - ${FONT_SETTING_LABELS[key] || key}`}
                              value={getFontSelectValue(key)}
                              onChange={handleFontSettingSelect(key)}
                              SelectProps={{ native: true }}
                            >
                              {options.map((option, index) => (
                                <option value={index} key={`${key}-${index}`}>
                                  {describeFontOption(option)}
                                </option>
                              ))}
                            </TextField>
                          </Grid>
                        ))}
                      </Grid>
                    </Grid>
                  )}
                */}

                {/*
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Одберете фонт за вашата покана
                    </Typography>
                    <Grid container spacing={2}>
                      {FONT_SETTING_KEYS.map((key) => {
                        const hasFamily = Boolean(getCustomFontFamilyValue(key));
                        const styles = getGoogleFontStylesForField(key);
                        const weights = getGoogleFontWeightsForField(key);
                        return (
                          <Grid item xs={12} md={4} key={`google-font-${key}`}>
                            <Stack spacing={1.5}>
                              <TextField
                                select
                                fullWidth
                                label={`Google фонт - ${FONT_SETTING_LABELS[key] || key}`}
                                value={getCustomFontFamilyValue(key)}
                                onChange={handleCustomFontFamilyChange(key)}
                                SelectProps={{ native: true }}
                                InputLabelProps={{ shrink: true }}
                              >
                                <option value="">Стандардно (шаблон)</option>
                                {GOOGLE_FONT_FAMILY_OPTIONS.map((option) => (
                                  <option value={option.value} key={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </TextField>

                              <TextField
                                select
                                fullWidth
                                label="Дебелина"
                                value={hasFamily ? getCustomFontWeightValue(key) : ""}
                                onChange={handleCustomFontWeightChange(key)}
                                SelectProps={{ native: true }}
                                disabled={!hasFamily || !weights.length}
                                InputLabelProps={{ shrink: true }}
                              >
                                {weights.map((weight) => (
                                  <option value={weight} key={`${key}-weight-${weight}`}>
                                    {weight}
                                  </option>
                                ))}
                              </TextField>

                              <TextField
                                select
                                fullWidth
                                label="Стил"
                                value={hasFamily ? getCustomFontStyleValue(key) || "normal" : ""}
                                onChange={handleCustomFontStyleChange(key)}
                                SelectProps={{ native: true }}
                                disabled={!hasFamily || styles.length <= 1}
                                InputLabelProps={{ shrink: true }}
                              >
                                {styles.map((style) => (
                                  <option value={style} key={`${key}-style-${style}`}>
                                    {style === "italic" ? "Italic" : "Regular"}
                                  </option>
                                ))}
                              </TextField>
                            </Stack>
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Grid>
                */}

                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Насловна фотографија
                  </Typography>
                  <Stack spacing={2} sx={{ maxWidth: { xs: "none", md: 720 }, mx: { xs: 0, md: "auto" } }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={2}
                      alignItems={{ xs: "stretch", sm: "center" }}
                    >
                      <Button
                        variant="outlined"
                        component="label"
                        sx={{
                          color: "var(--primary-color)",
                          borderColor: "var(--primary-color)",
                          "&:hover": {
                            borderColor: "var(--primary-color-dark)",
                            backgroundColor: "rgba(227, 95, 135, 0.04)",
                          },
                        }}
                      >
                        Одберете фотографија
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={handleCoverFileChange}
                          ref={coverInputRef}
                        />
                      </Button>
                      {(coverFile || coverPreview) && (
                        <Typography variant="body2" color="text.secondary">
                          {coverFile
                            ? coverFile.name
                            : "Користена е тековната фотографија."}
                        </Typography>
                      )}
                      {coverFile && (
                        <Button color="secondary" onClick={handleCoverRemove}>
                          Отстрани избор
                        </Button>
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Поддржани формати: JPG, PNG, WEBP (до 10MB).
                    </Typography>
                    {coverUploadError && (
                      <Typography variant="body2" color="error">
                        {coverUploadError}
                      </Typography>
                    )}
                    {coverPreview && (
                      <Box sx={{ width: { xs: "100%", md: "50%" } }}>
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 2,
                            overflow: "hidden",
                            width: "100%",
                            cursor: "pointer",
                          }}
                          onClick={handleCoverPreviewClick}
                        >
                          <img
                            src={coverPreview}
                            alt="Преглед на насловна фотографија"
                            style={{
                              maxHeight: "100px",
                              maxWidth: "100%",
                              width: "auto",
                              display: "block",
                              margin: "0 auto",
                            }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {coverFile
                            ? "Новата фотографија ќе се прикаже по зачувување."
                            : "Оваа фотографија е тековно активна."}
                          <br />
                          Кликнете за преглед на цел екран.
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </Grid>

                {/* Message */}
                <Grid item xs={12}>
                  {quotes.length > 0 && (
                    <TextField
                      select
                      fullWidth
                      label="Избери готова порака"
                      value={applyingQuoteId}
                      onChange={(event) => {
                        const value = event.target.value;
                        setApplyingQuoteId(value);
                        const quote = quotes.find((q) => q.key === value);
                        if (quote?.text) {
                          setForm((prev) => ({
                            ...prev,
                            message: quote.text,
                          }));
                        }
                      }}
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        maxWidth: { xs: "none", md: 720 },
                        mx: { xs: 0, md: "auto" },
                        mb: 2,
                      }}
                    >
                      <option value="">-- Одбери --</option>
                      {quotes.map((quote) => (
                        <option key={quote.key} value={quote.key}>
                          {quote.text || "Порака"}
                        </option>
                      ))}
                    </TextField>
                  )}
                  <TextField
                    fullWidth
                    label="Порака"
                    value={form.message}
                    onChange={handleChange("message")}
                    multiline
                    minRows={4}
                    sx={{ maxWidth: { xs: 'none', md: 720 }, mx: { xs: 0, md: 'auto' } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <InvitationAgendaEditor
                    agendaShown={form.agenda_shown}
                    agendaItems={form.agenda_items}
                    onAgendaShownChange={handleAgendaShownChange}
                    onAgendaItemsChange={handleAgendaItemsChange}
                  />
                </Grid>

                {/*
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Бои
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <TextField
                          type="color"
                          label="Примарна боја"
                          value={form.primary_color || selectedPaletteDefaults.primary}
                          onChange={handleColorChange("primary_color")}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          helperText={form.primary_color || selectedPaletteDefaults.primary}
                          inputProps={{ style: { padding: 0, height: "56px" } }}
                          sx={COLOR_PICKER_SX}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField
                          type="color"
                          label="Акцент боја"
                          value={form.secondary_color || selectedPaletteDefaults.secondary}
                          onChange={handleColorChange("secondary_color")}
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          helperText={form.secondary_color || selectedPaletteDefaults.secondary}
                          inputProps={{ style: { padding: 0, height: "56px" } }}
                          sx={COLOR_PICKER_SX}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                */}

                {/*
                <Grid item xs={12} sx={{ display: "flex", justifyContent: "center" }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={form.is_public}
                        onChange={handleChange("is_public")}
                      />
                    }
                    label="Поканата е јавна"
                  />
                </Grid>
                */}

                {selectedTemplate?.name && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Активен шаблон: {selectedTemplate.name}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Stack direction="row" spacing={2}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={submitting}
                      sx={{
                        backgroundColor: "var(--primary-color)",
                        "&:hover": {
                          backgroundColor: "var(--primary-color-dark)",
                        },
                      }}
                    >
                      {submitting
                        ? "Се зачувува..."
                        : isEdit
                        ? "Зачувај измени"
                        : "Креирај покана"}
                    </Button>
                    <Button
                      onClick={() => navigate("/invitations")}
                      sx={{
                        color: "var(--primary-color)",
                        "&:hover": {
                          backgroundColor: "rgba(227, 95, 135, 0.08)",
                        },
                      }}
                    >
                      Откажи
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
        )}
      </Box>
      {coverPreview && (
        <Dialog
          open={isCoverPreviewDialogOpen}
          onClose={handleCoverPreviewDialogClose}
          maxWidth="md"
          fullWidth
        >
          <DialogContent sx={{ p: 0 }}>
            <img
              src={coverPreview}
              alt="Насловна фотографија"
              style={{ width: "100%", display: "block" }}
            />
          </DialogContent>
        </Dialog>
      )}
    </>
  );
};

export default InvitationBuilder;
